import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import {
  CreateRoleProfileSchema,
  RoleProfileSchema,
  UpdateRoleProfileSchema,
  type CreateRoleProfileInput,
  type RoleProfile,
  type UpdateRoleProfileInput,
} from '@datazo/contracts';
import { apiFetch } from '@/shared/api/client';

const RoleListSchema = z.array(RoleProfileSchema);

export const rolesKeys = {
  all: ['roles'] as const,
};

export function useRoles(): UseQueryResult<RoleProfile[], Error> {
  return useQuery({
    queryKey: rolesKeys.all,
    queryFn: async () =>
      apiFetch<RoleProfile[]>('/api/roles', { parseAs: RoleListSchema }),
    staleTime: 30_000,
  });
}

export function useCreateRole(): UseMutationResult<RoleProfile, Error, CreateRoleProfileInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const body = CreateRoleProfileSchema.parse(input);
      return apiFetch<RoleProfile>('/api/roles', {
        method: 'POST',
        body,
        parseAs: RoleProfileSchema,
      });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: rolesKeys.all }),
  });
}

export function useUpdateRole(): UseMutationResult<
  RoleProfile,
  Error,
  { id: string; input: UpdateRoleProfileInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }) => {
      const body = UpdateRoleProfileSchema.parse(input);
      return apiFetch<RoleProfile>(`/api/roles/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body,
        parseAs: RoleProfileSchema,
      });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: rolesKeys.all }),
  });
}

export function useDeleteRole(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      apiFetch<void>(`/api/roles/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: rolesKeys.all }),
  });
}
